<script lang="ts">
    /**
     * DirectCanvas Wrapper with CanvasEditor
     *
     * This component creates CanvasEditor + DirectCanvas and integrates with the Rool app.
     *
     * Architecture:
     * 1. App stores (nodes, edges) maintain UINode/UIEdge data (top-left positions)
     * 2. Adapter converts UINode ↔ AppNode (minimal — both use top-left)
     * 3. CanvasEditor manages everything:
     *    - DirectCanvas rendering
     *    - Behaviors (selection, drag, clipboard, resize, rotate, connect)
     *    - Camera (zoom/pan, keyboard shortcuts)
     *    - Z-order, cursors, overlays, box selection
     *    - Grid snapping
     * 4. State changes flow back to app stores
     * 5. Changes persist to Rool SDK via unified onChange handler
     *
     * Data Flow:
     * - nodes store → uiNodeToAppNode → editor (via setNodes)
     * - editor onChange → handleChange → Rool SDK persistence
     * - editor onSelectionChange → selectedNodeIds store
     */
    import { onMount, onDestroy } from 'svelte';
    import { get } from 'svelte/store';
    import type { RoolChannel } from '@rool-dev/sdk';
    import { type DirectCanvas, type Camera } from '@rool/renderer';
    import { CanvasEditor, createContentEditablePlugin, createHeaderEditingPlugin, createTiptapEditingPlugin, createObjectEditingPlugin, createStickyNoteEditingPlugin, type Point, type CanvasChangeEvent, getAutoFitSettings } from '@rool/editor';
    import type { ViewState } from '@rool/editor';

    import {
        uiNodeToAppNode,
        uiEdgeToAppEdge,
        type AppNode,
        type AppEdge,
    } from '../../lib/canvas-adapter';
    import { nodes, edges, space, selectedNodeIds, viewport, canvasInstance, canvasEditor, activeToolId, focusNodeRequest, editNodeRequest, theme, gridStyle, gridSnapping } from '../../stores';
    import { registerAppRenderers, appRenderersRegistered } from '../../renderers';
    import { computeStickyNoteFontSize, MIN_STICKY_FONT_SIZE, rendererRegistry } from '@rool/renderer';
    import '../../canvas.css';
    import * as canvasMutations from '../../lib/canvas-mutations';
    import { getCachedMediaUrl, resolveMediaUrl, proxyMediaUrl } from '../../lib/media';
    import * as fileUpload from '../../lib/file-upload';
    import * as toast from '../../lib/toast';

    // =============================================================================
    // Constants
    // =============================================================================

    const MIN_ZOOM = 0.1;
    const MAX_ZOOM = 5;
    const GRID_SIZE = 20;
    const AUTO_FIT_ENABLED = false;

    let container: HTMLElement = $state() as HTMLElement;
    let editor: CanvasEditor | null = null;
    let canvas: DirectCanvas | null = null;

    // Track previous editing state to detect editing changes from ViewState
    let wasEditingNodeId: string | null = null;

    // Track whether we've done the initial camera fit
    let hasInitialFit = false;

    // =============================================================================
    // Data Layer - mutable arrays accessed by CanvasEditor via callbacks
    // =============================================================================

    let appNodes: AppNode[] = [];
    let appEdges: AppEdge[] = [];

    // =============================================================================
    // Helper: find appNode by ID
    // =============================================================================

    function findAppNode(nodeId: string): AppNode | undefined {
        return appNodes.find(n => n.id === nodeId);
    }

    // =============================================================================
    // Debug API
    // =============================================================================

    function setupDebugAPI(dc: DirectCanvas) {
        const debugAPI = {
            toggle: () => {
                dc.toggleDebug();
                return dc.getDebugOptions().showPanel ? 'Debug panel shown' : 'Debug panel hidden';
            },
            show: () => { dc.setDebugOptions({ showPanel: true }); return 'Debug panel shown'; },
            hide: () => { dc.setDebugOptions({ showPanel: false }); return 'Debug panel hidden'; },
            options: () => dc.getDebugOptions(),
            set: (options: Parameters<typeof dc.setDebugOptions>[0]) => {
                dc.setDebugOptions(options);
                return dc.getDebugOptions();
            },
            lod: (enabled?: boolean) => {
                if (enabled === undefined) return dc.getSimplifyLOD();
                dc.setSimplifyLOD(enabled);
                return enabled ? 'LOD enabled' : 'LOD disabled';
            },
            showLODColors: (show = true) => {
                dc.setDebugOptions({ showLODColors: show });
                return show ? 'LOD colors shown' : 'LOD colors hidden';
            },
            showRenderMode: (show = true) => {
                dc.setDebugOptions({ showRenderMode: show });
                return show ? 'Render mode visualization shown' : 'Render mode visualization hidden';
            },
            get canvas() { return dc; },
            getSpace: () => get(space),
            help: () => {
                console.log(`Canvas Debug API\n================\ncanvasDebug.toggle/show/hide/options/set/lod/showLODColors/showRenderMode/canvas/getSpace`);
                return 'See console for help';
            }
        };
        (window as any).canvasDebug = debugAPI;
        console.log('[canvas] Debug API available: window.canvasDebug (try canvasDebug.help())');
    }

    function cleanupDebugAPI() {
        delete (window as any).canvasDebug;
    }

    // =============================================================================
    // Unified onChange Handler — single handler for all editor data mutations
    // =============================================================================

    async function handleChange(event: CanvasChangeEvent) {
        switch (event.type) {
            case 'move': {
                canvasMutations.moveNodes(event.nodes);
                break;
            }

            case 'resize': {
                // Collect autofit flags from the local appNode (set by resizeNode)
                // before updating the store, so we can include them in both the
                // store update and the SDK persist.
                const resizedAppNode = findAppNode(event.nodeId);
                const resizeObj = (resizedAppNode?.data?.obj as Record<string, unknown> | undefined)
                    ?? resizedAppNode?.data;
                const autofitData: Record<string, unknown> = {};
                if (resizeObj) {
                    for (const key of ['autoFitWidth', 'autoFitHeight', 'autoSizeText', 'wordWrap'] as const) {
                        if (typeof resizeObj[key] === 'boolean') autofitData[key] = resizeObj[key];
                    }
                    if (resizedAppNode?.type === 'header' && resizeObj.manuallyResized) {
                        autofitData.manuallyResized = true;
                    }
                }
                canvasMutations.resizeNode(event.nodeId, event.position, event.size, autofitData);
                break;
            }

            case 'rotate': {
                canvasMutations.rotateNode(event.nodeId, event.rotation);
                break;
            }

            case 'delete-nodes': {
                canvasMutations.deleteNodes(event.nodeIds);
                break;
            }

            case 'delete-edges': {
                canvasMutations.deleteEdges(event.edgeIds);
                break;
            }

            case 'create-edge': {
                await canvasMutations.createEdge(event.sourceNodeId, event.targetNodeId);
                break;
            }

            case 'layout': {
                canvasMutations.layoutNodes(event.movedNodes);
                break;
            }

            case 'action': {
                if (event.action === 'close') {
                    canvasMutations.deleteNode(event.nodeId);
                } else if (event.action === 'pdf-prev' || event.action === 'pdf-next') {
                    handlePdfPageChange(event.nodeId, event.action === 'pdf-next' ? 1 : -1);
                }
                break;
            }
        }
    }

    function handleSelectionChange(selectedIds: string[]) {
        selectedNodeIds.set(new Set(selectedIds));
    }

    // =============================================================================
    // PDF Page Navigation
    // =============================================================================

    async function handlePdfPageChange(nodeId: string, delta: number) {
        const currentSpace = get(space) as any as RoolChannel;
        if (!currentSpace) return;

        const obj = await currentSpace.getObject(nodeId);
        if (!obj) return;

        const currentPage = (obj.currentPage as number) || 1;
        const totalPages = (obj.totalPages as number) || 1;
        const newPage = Math.max(1, Math.min(currentPage + delta, totalPages));

        if (newPage === currentPage) return;

        await canvasMutations.persistNodeData(nodeId, { currentPage: newPage }, 'change-pdf-page');
    }

    // =============================================================================
    // Image Error Handler — on-demand proxy for CORS-blocked external images
    // =============================================================================

    /** Track img elements already being proxied to avoid duplicate work */
    const proxyingImages = new WeakSet<HTMLImageElement>();

    /**
     * Capture-phase error listener for <img> inside canvas nodes.
     * Safety net: if an image fails to load despite the preload CORS probe,
     * try fetching it through the SDK's server-side proxy.
     */
    function handleImageError(e: Event) {
        const img = e.target as HTMLImageElement;
        if (img.tagName !== 'IMG') return;
        if (!img.closest?.('.canvas-node')) return;

        const src = img.getAttribute('src') || '';
        if (!src || src.startsWith('blob:') || src.startsWith('data:')) return;
        if (proxyingImages.has(img)) return;
        proxyingImages.add(img);

        proxyMediaUrl(src).then((blobUrl) => {
            if (blobUrl && img.isConnected) {
                img.src = blobUrl;
                import('../../lib/graph').then(({ rebuildGraph }) => {
                    setTimeout(() => rebuildGraph(), 0);
                });
            }
        });
    }

    // =============================================================================
    // Clipboard Handlers
    // =============================================================================

    function handleGetNodeData(nodeId: string): { type: string; size: { width: number; height: number }; rotation?: number; data?: unknown } | undefined {
        const node = findAppNode(nodeId);
        if (!node) return undefined;
        const nodeData = node.data as { id?: string; obj?: Record<string, unknown> } | undefined;
        const objData = nodeData?.obj;
        return {
            type: node.type,
            size: { ...node.size },
            rotation: node.rotation,
            data: objData ? JSON.parse(JSON.stringify(objData)) : undefined,
        };
    }

    async function handleCreateNodeWithData(
        nodeData: { type: string; size: { width: number; height: number }; rotation?: number; data?: unknown },
        position: Point
    ): Promise<string> {
        const currentSpace = get(space) as any as RoolChannel;
        if (!currentSpace) throw new Error('No space available for paste');

        const clonedNodeData = JSON.parse(JSON.stringify(nodeData));
        // Position is already top-left
        const topLeftX = position.x;
        const topLeftY = position.y;

        const nodeDataObj = clonedNodeData.data as Record<string, unknown> | undefined;
        const {
            id: _stripId, nodeId: _stripNodeId, objectId: _stripObjectId,
            x: _stripX, y: _stripY, width: _stripWidth, height: _stripHeight,
            _x: _strip_X, _y: _strip_Y, _width: _strip_W, _height: _strip_H, _rotation: _strip_R,
            ...cleanData
        } = nodeDataObj ?? {};

        currentSpace.checkpoint('paste-node');
        const rotation = clonedNodeData.rotation;
        const result = await currentSpace.createObject({
            data: {
                ...cleanData,
                type: clonedNodeData.type,
                _x: topLeftX, _y: topLeftY,
                _width: clonedNodeData.size.width, _height: clonedNodeData.size.height,
                ...(rotation ? { _rotation: rotation } : {}),
            },
        });

        const nodeId = result.object.id;

        // Add to local appNodes so the editor can see it immediately
        const label = (cleanData.label as string) ?? (cleanData.title as string) ?? (cleanData.content as string) ?? clonedNodeData.type;
        const nodeDataObj2: Record<string, unknown> = {
            id: nodeId,
            obj: {
                ...cleanData,
                type: clonedNodeData.type,
                x: topLeftX, y: topLeftY,
                width: clonedNodeData.size.width, height: clonedNodeData.size.height,
            },
        };

        // For image nodes, set src using fetchMedia cache so the renderer
        // can display the image immediately (matches rebuildGraph logic)
        if (clonedNodeData.type === 'image') {
            const cd = cleanData as Record<string, unknown>;
            const originalUrl = (cd.url ?? cd.content ?? cd.src ?? cd.image ?? '') as string;
            nodeDataObj2.src = getCachedMediaUrl(originalUrl);
            nodeDataObj2._originalSrc = originalUrl;
            if (cd.title) nodeDataObj2.title = cd.title;
            if (cd.caption) nodeDataObj2.caption = cd.caption;
            if (cd.alt) nodeDataObj2.alt = cd.alt;
            if (cd.objectFit) nodeDataObj2.objectFit = cd.objectFit;
            if (cd.labelPosition) nodeDataObj2.labelPosition = cd.labelPosition;
            if (cd.showLabels) nodeDataObj2.showLabels = cd.showLabels;
        }

        // The SDK emits objectCreated synchronously during createObject(),
        // which triggers rebuildGraph() and may have already added this node
        // to appNodes. Only add it if it's not already present to avoid
        // duplicates (which cause position-revert bugs during drag).
        if (!findAppNode(nodeId)) {
            const newAppNode: AppNode = {
                id: nodeId,
                type: clonedNodeData.type,
                position: { x: topLeftX, y: topLeftY },
                z: 0,
                size: { width: clonedNodeData.size.width, height: clonedNodeData.size.height },
                rotation: rotation || undefined,
                data: nodeDataObj2,
                label,
            };
            appNodes = [...appNodes, newAppNode];
        }
        editor?.setNodes(appNodes);

        if (AUTO_FIT_ENABLED) {
            requestAnimationFrame(() => {
                editor?.autoFitNodeAsync(nodeId);
            });
        }

        return nodeId;
    }

    // =============================================================================
    // External Paste Handler
    // =============================================================================

    async function handleExternalPaste(
        data: { type: 'image'; blob: Blob; position: Point } | { type: 'text'; text: string; position: Point }
    ) {
        const currentSpace = get(space) as any as RoolChannel;
        if (!currentSpace) return;

        if (data.type === 'image') {
            const toastId = toast.show('Uploading image...', { duration: 0 });
            try {
                const mediaUrl = await currentSpace.uploadMedia(data.blob);
                currentSpace.checkpoint('paste-image');
                await currentSpace.createObject({
                    data: {
                        type: 'image',
                        url: mediaUrl,
                        _x: data.position.x,
                        _y: data.position.y,
                    },
                });
                toast.update(toastId, { message: 'Upload complete', duration: 2000 });
                // rebuildGraph will be triggered by the SDK's objectCreated event
            } catch (err) {
                console.error('[canvas] Failed to paste image:', err);
                toast.update(toastId, { message: 'Upload failed', duration: 3000 });
            }
        }
        // Text paste not currently supported (external text is not converted to nodes)
    }

    // =============================================================================
    // File Drop Handler
    // =============================================================================

    async function handleFileDrop(data: { files: File[]; position: { x: number; y: number } }) {
        const currentSpace = get(space) as any as RoolChannel;
        if (!currentSpace) return;

        const createdNodeIds = await fileUpload.uploadFiles(
            currentSpace,
            data.files,
            data.position,
            'drop-files',
        );

        // Focus and select the dropped nodes
        if (createdNodeIds.length > 0) {
            focusNodeRequest.set(createdNodeIds[0]);
            requestAnimationFrame(() => {
                if (editor) editor.select(createdNodeIds);
            });
        }
    }

    async function handleGetNodeImageBlob(nodeId: string): Promise<Blob | null> {
        const node = findAppNode(nodeId);
        if (!node || node.type !== 'image') return null;

        const originalSrc = (node.data as Record<string, unknown>)?._originalSrc as string | undefined;
        if (!originalSrc) return null;

        try {
            const resolvedUrl = await resolveMediaUrl(originalSrc);
            if (!resolvedUrl) return null;
            const response = await fetch(resolvedUrl);
            return await response.blob();
        } catch (err) {
            console.error('[canvas] Failed to get image blob for clipboard:', err);
            return null;
        }
    }

    // =============================================================================
    // Creation Tool Support
    // =============================================================================

    /** Default sizes for node types when created via toolbar tool */
    const NODE_DEFAULT_SIZES: Record<string, { width: number; height: number }> = {
        'sticky-note': { width: 200, height: 200 },
        'header': { width: 400, height: 80 },
        'markdown': { width: 320, height: 220 },
    };

    /**
     * Create a node via the Space SDK. Called by the editor's tool system
     * when a creation tool is active and the user clicks the canvas.
     * Position is the world-space center of where the click occurred.
     */
    function createNodeViaSDK(type: string, position: { x: number; y: number }): void {
        const currentSpace = get(space) as any as RoolChannel;
        if (!currentSpace) return;

        const defaults = NODE_DEFAULT_SIZES[type] ?? { width: 200, height: 200 };
        // Position is click point — convert to top-left
        const x = position.x - defaults.width / 2;
        const y = position.y - defaults.height / 2;

        // Fire-and-forget async creation
        (async () => {
            try {
                currentSpace.checkpoint('create-node');
                const result = await currentSpace.createObject({
                    data: {
                        type,
                        content: '',
                        _x: x,
                        _y: y,
                        _width: defaults.width,
                        _height: defaults.height,
                    },
                });

                const nodeId = result.object.id;

                // Focus, select, and start editing the new node
                requestAnimationFrame(() => {
                    if (!editor) return;
                    focusNodeRequest.set(nodeId);
                    editNodeRequest.set(nodeId);
                });
            } catch (err) {
                console.error('[canvas] Failed to create node:', err);
            }
        })();
    }

    // =============================================================================
    // Data Mutation Callbacks (called by CanvasEditor behaviors)
    // =============================================================================

    function moveNodes(nodeIds: string[], offset: Point): void {
        // Offset is already snapped by the editor when grid snapping is enabled
        for (const nodeId of nodeIds) {
            const node = findAppNode(nodeId);
            if (node) {
                node.position.x += offset.x;
                node.position.y += offset.y;
            }
        }
    }

    function duplicateNodes(nodeIds: string[], offset: Point): string[] {
        // Offset is already snapped by the editor when grid snapping is enabled
        const duplicatedNodes: AppNode[] = [];
        const idMap = new Map<string, string>();

        for (const nodeId of nodeIds) {
            const original = findAppNode(nodeId);
            if (original) {
                const newId = `${original.id}-copy-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
                idMap.set(original.id, newId);
                duplicatedNodes.push({
                    ...original,
                    id: newId,
                    position: {
                        x: original.position.x + offset.x,
                        y: original.position.y + offset.y,
                    },
                });
            }
        }
        appNodes.push(...duplicatedNodes);

        // Duplicate edges between duplicated nodes
        const draggedSet = new Set(nodeIds);
        const newEdges: AppEdge[] = [];
        for (const edge of appEdges) {
            if (draggedSet.has(edge.sourceNodeId) && draggedSet.has(edge.targetNodeId)) {
                newEdges.push({
                    id: `${edge.id}-copy-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                    type: edge.type,
                    source: idMap.get(edge.sourceNodeId)!,
                    target: idMap.get(edge.targetNodeId)!,
                    sourceNodeId: idMap.get(edge.sourceNodeId)!,
                    targetNodeId: idMap.get(edge.targetNodeId)!,
                    data: edge.data ?? {},
                });
            }
        }
        appEdges.push(...newEdges);

        return duplicatedNodes.map(n => n.id);
    }

    function deleteNodesLocal(nodeIds: string[]): void {
        const deletedSet = new Set(nodeIds);
        appNodes = appNodes.filter(n => !deletedSet.has(n.id));
        appEdges = appEdges.filter(e => !deletedSet.has(e.sourceNodeId) && !deletedSet.has(e.targetNodeId));
        // Persistence handled by onChange
    }

    function deleteEdgesLocal(edgeIds: string[]): void {
        const deletedSet = new Set(edgeIds);
        appEdges = appEdges.filter(e => !deletedSet.has(e.id));
        // Persistence handled by onChange
    }

    function createEdgeLocal(sourceNodeId: string, targetNodeId: string): void {
        // Persistence handled by onChange
    }

    function getNodeRotation(nodeId: string): number {
        const node = findAppNode(nodeId);
        return node?.rotation ?? 0;
    }

    function rotateNode(nodeId: string, rotation: number): void {
        const node = findAppNode(nodeId);
        if (node) {
            node.rotation = rotation;
            // Persistence handled by onChange
        }
    }

    function resizeNode(nodeId: string, bounds: { cx: number; cy: number; width: number; height: number }): void {
        const node = findAppNode(nodeId);
        if (node) {
            // Bounds are already snapped by the editor's view-state when grid snapping is enabled
            const topLeft = { x: bounds.cx - bounds.width / 2, y: bounds.cy - bounds.height / 2 };

            // When the user manually resizes a node, update autofit settings.
            // Nodes already in autoSizeText mode (e.g. sticky notes) keep
            // their settings — the text just rescales to fill the new box.
            // Only applies to editable (text-based) node types — images/files
            // don't have text content so autofit flags are irrelevant.
            if (editor?.getViewState().resize && editor.getNodeTypeRegistry().isEditable(node.type)) {
                const sizeChanged = Math.abs(bounds.width - node.size.width) > 2 ||
                                    Math.abs(bounds.height - node.size.height) > 2;
                if (sizeChanged) {
                    const settings = getAutoFitSettings(node as any);
                    if (!settings.autoSizeText) {
                        const obj = (node.data.obj as Record<string, unknown> | undefined) ?? node.data;
                        if (node.type === 'header') {
                            // Headers: switch to auto-size-text mode with word wrap.
                            // The font scales to fill the user-chosen box size.
                            obj.autoFitWidth = false;
                            obj.autoFitHeight = false;
                            obj.autoSizeText = true;
                            obj.wordWrap = true;
                            obj.manuallyResized = true;
                        } else {
                            // Other nodes: keep height auto-fit, lock width.
                            obj.autoFitWidth = false;
                            obj.autoFitHeight = true;
                            obj.autoSizeText = false;
                        }
                    }
                }
            }

            node.position.x = topLeft.x;
            node.position.y = topLeft.y;
            node.size.width = bounds.width;
            node.size.height = bounds.height;
        }
        // Persistence handled by onChange
    }

    // =============================================================================
    // State Change Handlers
    // =============================================================================

    function handleStateChange(state: ViewState) {
        if (!editor) return;

        // Detect editing state changes from ViewState
        const editingNodeId = state.editing?.nodeId ?? null;
        if (editingNodeId !== wasEditingNodeId) {
            const prevEditingNodeId = wasEditingNodeId;
            // Update tracking BEFORE re-entrant calls to prevent infinite recursion
            // (handleEditRequest → updateState → onViewStateChange → here again)
            wasEditingNodeId = editingNodeId;
            if (prevEditingNodeId && !editingNodeId) {
                // ViewState editing was cleared — stop the editing manager session
                editor.getEditingManager()?.stopEditing(true);
            } else if (editingNodeId && !prevEditingNodeId) {
                // ViewState editing was started — start via editing manager
                editor.handleEditRequest(editingNodeId);
            }
        }
    }

    function handleCameraChange(camera: Camera) {
        viewport.set(camera);
    }

    function handleToolChange(toolState: { activeTool: string | null; locked: boolean }) {
        activeToolId.set(toolState.activeTool);
    }

    // =============================================================================
    // Editing Callbacks
    // =============================================================================

    /**
     * Called by the EditingManager when editing ends.
     * Handles:
     * - Auto-fit header resize (before re-render)
     * - Renderer cache cleanup
     * - SDK persistence
     */
    function handleEditEnd(
        nodeId: string,
        content: string,
        size?: { width: number; height: number },
    ) {
        const node = findAppNode(nodeId);
        if (!node) return;

        // Auto-fit after content change for all editable node types.
        // Uses editor.autoFitNode() which respects the per-node autofit settings
        // (autoFitWidth, autoFitHeight, autoSizeText flags from the Auto-Fit menu).
        // Height is grow-only: editing never shrinks a node's height. During editing,
        // onGrowNode already grew the node to fit content; here we preserve that.
        const isObject = node.type === 'object';
        let autoFitSize: { width: number; height: number } | null = null;

        // TODO: Re-enable autofit on edit-end once the position-shift issue is resolved.
        // autoFitNode uses center-anchored resizing which shifts node.position when
        // the measured content size differs from the current size — this causes a
        // visible jump when deselecting freshly placed headers/markdown nodes.
        if (!isObject) {
            const obj = node.data.obj as Record<string, unknown> | undefined;
            if (obj) obj.content = content;
        }

        // Persist content to SDK (use auto-fit size if available, otherwise plugin size)
        if (node.type === 'object') {
            handleObjectFieldSave(nodeId, content);
        } else {
            handleContentSave(nodeId, content, autoFitSize ?? size);
        }

        // Clear ViewState editing (if still set)
        if (editor?.isEditing()) { editor.stopEditing(); }
    }

    /**
     * Persist edited object fields back to the SDK.
     * The content is a JSON string mapping field keys to their edited text values.
     */
    async function handleObjectFieldSave(nodeId: string, content: string) {
        if (!content) return;
        try {
            const editedFields: Record<string, string> = JSON.parse(content);
            const node = findAppNode(nodeId);
            const obj = (node?.data?.obj as Record<string, unknown>) ?? {};
            // Merge edited text values back, matching keys case-insensitively
            const updates: Record<string, unknown> = {};
            for (const [editKey, editValue] of Object.entries(editedFields)) {
                // Find the original key (field keys are displayed uppercase)
                const originalKey = Object.keys(obj).find(
                    k => k.toLowerCase() === editKey.toLowerCase()
                );
                if (originalKey) {
                    updates[originalKey] = editValue;
                    if (obj) (obj as Record<string, unknown>)[originalKey] = editValue;
                }
            }
            if (Object.keys(updates).length > 0) {
                await canvasMutations.persistNodeData(nodeId, updates, 'edit-object');
            }
        } catch (error) {
            console.error('Error saving object fields:', error);
        }
    }

    /**
     * Called by the EditingManager when a node needs to be invalidated after editing.
     * Handles renderer cache cleanup and canvas invalidation.
     */
    function handleInvalidateNodeAfterEdit(nodeId: string) {
        if (!canvas) return;
        const node = findAppNode(nodeId);
        const nodeType = node?.type ?? 'topic';
        const renderer = rendererRegistry.get(nodeType);
        renderer?.cleanup?.(nodeId);
        canvas.invalidateNode(nodeId);
    }

    async function handleContentSave(
        nodeId: string,
        content: string,
        size?: { width: number; height: number } | null,
    ) {
        try {
            const node = findAppNode(nodeId);
            const checkpointMsg = node?.type === 'header' ? 'edit-header'
                : node?.type === 'sticky-note' ? 'edit-sticky-note' : 'edit-note';

            const data: Record<string, unknown> = { content };
            if (size) { data._width = size.width; data._height = size.height; }

            await canvasMutations.persistNodeData(nodeId, data, checkpointMsg);
        } catch (error) {
            console.error('Error updating content:', error);
        }
    }

    // =============================================================================
    // Store Sync
    // =============================================================================

    function handleNodesChange() {
        if (!editor) return;

        const currentNodes = get(nodes);
        appNodes = currentNodes.map(uiNodeToAppNode);
        editor.setNodes(appNodes);

        // Fit camera to all nodes on first load
        if (!hasInitialFit && currentNodes.length > 0) {
            hasInitialFit = true;
            requestAnimationFrame(() => {
                editor?.fitToNodes(undefined, 80, { maxZoom: 1 });
            });
        }

        if (AUTO_FIT_ENABLED) {
            // Auto-fit nodes that need content-based sizing
            // (markdown/object/header nodes without explicit stored dimensions,
            //  respecting autofit settings — skip when autoSizeText or both axes off)
            const needsAutoFit = currentNodes.filter(n => {
                const data = (n.data as any)?.obj;
                const nodeType = n.type;
                const hasSize = typeof data?.width === 'number' && typeof data?.height === 'number';
                if (hasSize) return false;
                if (nodeType !== 'markdown' && nodeType !== 'object' && nodeType !== 'header') return false;
                if (nodeType === 'header' && data?.manuallyResized) return false;
                const settings = getAutoFitSettings(n as any);
                if (settings.autoSizeText) return false;
                if (!settings.autoFitWidth && !settings.autoFitHeight) return false;
                return true;
            });
            if (needsAutoFit.length > 0) {
                requestAnimationFrame(() => {
                    editor?.autoFitNodesAsync(needsAutoFit.map(n => n.id));
                });
            }
        }
    }

    function handleEdgesChange() {
        if (!editor) return;
        const currentEdges = get(edges);
        appEdges = currentEdges.map(uiEdgeToAppEdge);
        editor.setEdges(appEdges);
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================

    onMount(() => {
        if (!appRenderersRegistered()) {
            registerAppRenderers();
        }

        // Create CanvasEditor — editor creates and owns DirectCanvas + InputSystem
        editor = new CanvasEditor({
            container,
            canvasOptions: {
                minZoom: MIN_ZOOM,
                maxZoom: MAX_ZOOM,
                gridSize: GRID_SIZE,
                showGrid: true,
                simplifyLOD: false,
            },
            gridSnapping: true,
            // Hit testing is now internal (DOM-based when canvas is available)
            // Data is pushed via editor.setNodes() / editor.setEdges()
            createNode: createNodeViaSDK,
            deleteNodes: deleteNodesLocal,
            deleteEdges: deleteEdgesLocal,
            moveNodes,
            duplicateNodes,
            createEdge: createEdgeLocal,
            resizeNode,
            rotateNode,
            getNodeRotation,
            onChange: handleChange,
            onSelectionChange: handleSelectionChange,
            onViewStateChange: handleStateChange,
            onCameraChange: handleCameraChange,
            onToolChange: handleToolChange,
            // Node conversion and content measurement are now internal to the editor
            // (uses defaultNodeToUINode/defaultEdgeToUIEdge + canvas.measureNodeContent)
            getNodeData: handleGetNodeData,
            createNodeWithData: handleCreateNodeWithData,
            onExternalPaste: handleExternalPaste,
            onFileDrop: handleFileDrop,
            getNodeImageBlob: handleGetNodeImageBlob,
            // Node type policies (shouldLockAspectRatio, isResizable, getIntrinsicAspectRatio,
            // getActualNodeSize, getNodeType) are now derived internally from the node store
            // and NodeTypeRegistry
            getGridSize: () => (editor?.getGridSnapping() ? GRID_SIZE : 0),
            // Editing plugins
            editingPlugins: [
                // Header nodes: per-keystroke resize/font-fit
                createHeaderEditingPlugin(),
                // Sticky notes: contentEditable with live font-size fitting + color toolbar
                createStickyNoteEditingPlugin({
                    onInput: (element, nodeId) => {
                        const node = findAppNode(nodeId);
                        if (!node) return;
                        // Only scale font dynamically when autoSizeText is enabled
                        const settings = getAutoFitSettings(node as any);
                        if (settings.autoSizeText) {
                            const text = element.innerText || '';
                            const fontSize = computeStickyNoteFontSize(
                                node.size.width, node.size.height, text,
                            );
                            element.style.fontSize = fontSize + 'px';
                            element.style.overflowY =
                                fontSize <= MIN_STICKY_FONT_SIZE ? 'auto' : 'hidden';
                        }
                    },
                    onColorChange: async (nodeId, color) => {
                        const node = findAppNode(nodeId);
                        if (!node) return;
                        // Update local data
                        const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
                        obj.color = color;
                        try {
                            await canvasMutations.persistNodeData(nodeId, { color }, 'change-sticky-color');
                        } catch (err) {
                            console.error('[canvas] Failed to persist sticky color:', err);
                        }
                        // Invalidate for re-render
                        if (canvas) {
                            const renderer = rendererRegistry.get('sticky-note');
                            renderer?.cleanup?.(nodeId);
                            canvas.invalidateNode(nodeId);
                        }
                    },
                }),
                // Markdown nodes: Tiptap rich-text editor
                createTiptapEditingPlugin({
                    getMarkdownContent: (nodeId) => {
                        const node = findAppNode(nodeId);
                        if (!node) return '';
                        const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
                        return typeof obj?.content === 'string' ? (obj.content as string) : '';
                    },
                    onGrowNode: (nodeId, editableElement, nodeElement) => {
                        if (!canvas) return;
                        const node = findAppNode(nodeId);
                        if (!node) return;
                        const contentHeight = editableElement.scrollHeight;
                        const style = getComputedStyle(nodeElement);
                        const paddingTop = parseFloat(style.paddingTop) || 0;
                        const paddingBottom = parseFloat(style.paddingBottom) || 0;
                        const neededHeight = contentHeight + paddingTop + paddingBottom;
                        if (Math.abs(neededHeight - node.size.height) < 2) return;
                        if (neededHeight <= node.size.height) return;
                        const cx = node.position.x + node.size.width / 2;
                        const cy = node.position.y + neededHeight / 2;
                        resizeNode(nodeId, { cx, cy, width: node.size.width, height: neededHeight });
                        editor?.render();
                    },
                }),
                // Object nodes: per-field inline editing
                createObjectEditingPlugin(),
            ],
            onEditEnd: handleEditEnd,
            invalidateNodeAfterEdit: handleInvalidateNodeAfterEdit,
        });

        // Get the internally-created canvas for direct access
        canvas = editor.getCanvas();

        // Listen for image load errors (capture phase, since error doesn't bubble)
        container.addEventListener('error', handleImageError, true);

        // Store editor and canvas for other components
        canvasInstance.set(canvas);
        canvasEditor.set(editor);
        if (canvas) setupDebugAPI(canvas);

        // Apply persisted grid snapping preference
        editor.setGridSnapping(get(gridSnapping));

        // Focus the canvas container so keyboard shortcuts work immediately
        container.focus();

        // Reset camera fit when switching spaces
        let previousSpaceId: string | null = null;
        const unsubSpace = space.subscribe((s) => {
            const newId = s?.id ?? null;
            if (previousSpaceId !== null && newId !== previousSpaceId) {
                hasInitialFit = false;
            }
            previousSpaceId = newId;
        });

        // Subscribe to stores and sync
        const unsubNodes = nodes.subscribe(handleNodesChange);
        const unsubEdges = edges.subscribe(handleEdgesChange);

        // Sync grid snapping from store (e.g., cross-tab changes)
        const unsubGridSnapping = gridSnapping.subscribe((enabled) => {
            if (editor) editor.setGridSnapping(enabled);
        });

        const unsubFocus = focusNodeRequest.subscribe((nodeId) => {
            if (nodeId && editor && canvas) {
                const currentNodes = get(nodes);
                const targetNode = currentNodes.find(n => n.id === nodeId);
                if (targetNode && targetNode.position) {
                    const size = targetNode.size ?? { width: 200, height: 100 };
                    const centerX = targetNode.position.x + size.width / 2;
                    const centerY = targetNode.position.y + size.height / 2;
                    editor.select([nodeId]);
                    editor.animateCamera({ x: centerX, y: centerY, zoom: 1 });
                }
                focusNodeRequest.set(null);
            }
        });

        const unsubEdit = editNodeRequest.subscribe((nodeId) => {
            if (nodeId && editor) {
                editor.handleEditRequest(nodeId);
                editNodeRequest.set(null);
            }
        });

        const unsubTheme = theme.subscribe((currentTheme) => {
            if (!canvas) return;
            if (currentTheme === 'dark') {
                canvas.setGridColors([0.102, 0.102, 0.102, 1.0], [0.3, 0.3, 0.3, 1.0]);
            } else {
                canvas.setGridColors([0.98, 0.98, 0.98, 1.0], [0.8, 0.8, 0.8, 1.0]);
            }
        });

        const unsubGridStyle = gridStyle.subscribe((style) => {
            canvas?.setGridStyle(style);
        });

        // Initial sync (initial camera fit is handled reactively in handleNodesChange)
        handleNodesChange();
        handleEdgesChange();

        return () => {
            unsubSpace();
            unsubNodes();
            unsubEdges();
            unsubFocus();
            unsubEdit();
            unsubTheme();
            unsubGridStyle();
            unsubGridSnapping();
        };
    });

    onDestroy(() => {
        container?.removeEventListener('error', handleImageError, true);
        cleanupDebugAPI();
        if (editor) { editor.destroy(); } // editor.destroy() cleans up editing manager + owned canvas
        canvasInstance.set(null);
        canvasEditor.set(null);
    });

    // =============================================================================
    // Exported methods
    // =============================================================================

    export function getCanvas(): DirectCanvas | null {
        return canvas;
    }

    export function fitToNodes(nodeIds?: string[], padding?: number) {
        editor?.fitToNodes(nodeIds, padding);
    }

    export function panTo(x: number, y: number) {
        if (!canvas) return;
        canvas.panTo(x, y);
        viewport.set(canvas.getCamera());
    }

    export function setCamera(camera: Partial<Camera>) {
        editor?.setCamera(camera, false);
    }

    export function startEditingNode(nodeId: string) {
        editor?.handleEditRequest(nodeId);
    }
</script>

<div bind:this={container} class="canvas-container"></div>

<style>
    .canvas-container {
        width: 100%;
        height: 100%;
        background: var(--bg0, #fafafa);
        outline: none;
    }
</style>
